import * as THREE from 'three/webgpu';
import {isFeatureLine, isPlanarFeatureFace} from '../../mesh/feature';
import {isSketchLineObject} from '../../mesh/sketch';
import {isDatumPlaneObject} from '../../reference/DatumPlane';
import type {PointSelectionCandidate} from '../../reference/reference-point/ReferencePointController';
import {SketchEntitiesTransformSelectionRole} from '../../sketch/entities-transform';
import type {RawPointerEventPayload} from '../../viewer/moudles/EventManager';
import type {EntitySelectionManager} from '../EntitySelectionManager';
import {AbstractSketchEntitiesTransformSelectionHandler} from './AbstractSketchEntitiesTransformSelectionHandler';

/**
 * 草图镜向实体选择处理器。
 *
 * 源实体复用通用变换选择；镜向轴入口覆盖活动草图直线、线性模型边、基准面和平面模型面。
 */
export class SketchMirrorEntitiesSelectionHandler extends AbstractSketchEntitiesTransformSelectionHandler {
    /**
     * @param entitySelectionManager 实体选择管理器。
     */
    public constructor(entitySelectionManager: EntitySelectionManager) {
        super(entitySelectionManager);
    }

    /**
     * 镜向轴阶段高亮入口类型合法的草图线或外部参考。
     *
     * @param payload 原始指针事件。
     */
    public override onMouseMove(payload: RawPointerEventPayload): void {
        if (this.resolveSelectionRole() !== SketchEntitiesTransformSelectionRole.MirrorAxis) {
            super.onMouseMove(payload);
            return;
        }
        const picked = this.pickMirrorAxisAt(payload.clientX, payload.clientY);
        this.updateHoverState(picked && this.isMirrorAxisSelectionObject(picked.object) ? picked : null);
    }

    /**
     * 镜向轴阶段把草图线或外部参考命中交给统一 resolver 校验。
     *
     * @param payload 原始指针事件。
     */
    public override onClick(payload: RawPointerEventPayload): void {
        if (payload.button !== 0) {
            return;
        }
        if (this.resolveSelectionRole() !== SketchEntitiesTransformSelectionRole.MirrorAxis) {
            super.onClick(payload);
            return;
        }
        const rawPicked = this.pickMirrorAxisAt(payload.clientX, payload.clientY);
        const picked = this.resolveClickSelectionPick(rawPicked);
        if (picked && this.isMirrorAxisSelectionObject(picked.object)) {
            this.entitySelectionManager.renderer.sketchMirrorEntitiesController?.handleMirrorAxisPick(picked.object);
        }
    }

    /** @returns 当前镜向选择角色。 */
    protected resolveSelectionRole(): SketchEntitiesTransformSelectionRole {
        return this.entitySelectionManager.renderer.sketchMirrorEntitiesController?.getSnapshot().activeSelectionRole
            ?? SketchEntitiesTransformSelectionRole.Entities;
    }

    /** @param object 当前命中的源实体。 */
    protected handleEntityPicked(object: THREE.Object3D): void {
        this.entitySelectionManager.renderer.sketchMirrorEntitiesController?.handleEntityPick(object);
    }

    /** @param objects 框选命中的源实体集合。 */
    protected handleEntitiesBoxSelected(objects: readonly THREE.Object3D[]): void {
        this.entitySelectionManager.renderer.sketchMirrorEntitiesController?.handleEntityBoxSelection(objects);
    }

    /**
     * 镜向实体没有自由参考点输入。
     *
     * @param candidate 基类传入的参考点候选，本模式明确忽略。
     */
    protected handleReferenceCandidate(candidate: PointSelectionCandidate): void {
        void candidate;
    }

    /** 镜像轴同时允许导入模型边和面，不受普通草图选择模式限制。 */
    protected override shouldEnableImportedTopologySelection(): boolean {
        return this.resolveSelectionRole() === SketchEntitiesTransformSelectionRole.MirrorAxis
            || super.shouldEnableImportedTopologySelection();
    }

    /** @returns 镜像轴 imported-model 二阶段统一按边或面解析。 */
    protected override resolveImportedTopologySelectionMode(): 'face-edge' {
        return 'face-edge';
    }

    /**
     * 收集镜像轴选择阶段的草图与外部参考对象。
     * @returns 可供统一 resolver 继续做几何与二维合法性校验的候选对象。
     */
    private collectMirrorAxisSelectionTargets(): THREE.Object3D[] {
        return this.mergeImportedTopologySelectionTargets(
            this.collectDatumReferenceInteractionTargets(this.collectClickSelectionTargets()),
        );
    }

    /**
     * 从草图/特征、导入拓扑与正式基准面三条拾取链中解析镜像轴命中。
     *
     * @param clientX 屏幕 X 坐标。
     * @param clientY 屏幕 Y 坐标。
     * @returns 已归一到 FeatureLine、FeatureFace 或 DatumPlane 根对象的命中。
     */
    private pickMirrorAxisAt(clientX: number, clientY: number): THREE.Intersection<THREE.Object3D> | null {
        const targets = this.collectMirrorAxisSelectionTargets();
        const entityPick = this.pickEntityAtForSelectionMode(clientX, clientY, targets, 'face-edge');
        const importedPick = this.pickImportedTopologyAt(clientX, clientY, targets);
        const datumPick = this.pickDatumReferenceAt(clientX, clientY, targets);
        return this.resolveSketchModePick(
            this.resolveSketchModePick(entityPick, datumPick),
            importedPick,
        );
    }

    /**
     * 判断命中对象是否属于镜像轴支持的入口类型。
     * 最终线性、共面和唯一交线规则仍由 controller 的稳定引用 resolver 统一判定。
     * @param object 当前命中对象。
     * @returns 属于支持入口类型时返回 true。
     */
    private isMirrorAxisSelectionObject(object: THREE.Object3D): boolean {
        return isSketchLineObject(object)
            || isFeatureLine(object)
            || isPlanarFeatureFace(object)
            || isDatumPlaneObject(object);
    }
}
